import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API } from '../../App';

const AdminContext = createContext(null);

export const useAdminData = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminData must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const navigate = useNavigate();
  
  // Core data states
  const [reviews, setReviews] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [activeInquiries, setActiveInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [selectedReviewFilter, setSelectedReviewFilter] = useState('all');
  const [selectedFeedbackFilter, setSelectedFeedbackFilter] = useState('all');
  const [includeCompletedInquiries, setIncludeCompletedInquiries] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const reviewParams = selectedReviewFilter === 'all' ? '' : `?status=${selectedReviewFilter}`;
      const feedbackParams = selectedFeedbackFilter === 'all' ? '' : `?status=${selectedFeedbackFilter}`;
      const inquiriesParams = includeCompletedInquiries ? '?include_completed=true' : '';

      const [
        reviewsRes,
        feedbackRes,
        usersRes,
        listingsRes,
        disputesRes,
        activeInquiriesRes,
        bookingsRes,
        transactionsRes
      ] = await Promise.all([
        axios.get(`${API}/admin/reviews${reviewParams}`, { headers }),
        axios.get(`${API}/admin/feedback${feedbackParams}`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/listings`, { headers }),
        axios.get(`${API}/admin/disputes`, { headers }),
        axios.get(`${API}/admin/inquiries/active${inquiriesParams}`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/payment-transactions`, { headers })
      ]);

      setReviews(reviewsRes.data);
      setFeedback(feedbackRes.data);
      setUsers(usersRes.data);
      setListings(listingsRes.data);
      setDisputes(disputesRes.data);
      setActiveInquiries(activeInquiriesRes.data);
      setBookings(bookingsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedReviewFilter, selectedFeedbackFilter, includeCompletedInquiries]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Review actions
  const approveReview = async (reviewId, approve) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/reviews/${reviewId}`,
        { is_approved: approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? 'Review approved' : 'Review rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review');
    }
  };

  const editReview = async (reviewId, editedComment) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/reviews/${reviewId}`,
        { is_approved: true, comment: editedComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review updated and approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit review');
    }
  };

  const hideReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to hide this review?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review hidden');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hide review');
    }
  };

  // Feedback actions
  const updateFeedbackStatus = async (feedbackId, status, comment = null) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { status };
      if (comment) payload.admin_comment = comment;
      
      await axios.patch(
        `${API}/admin/feedback/${feedbackId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feedback marked as ${status}${comment ? ' with resolution note' : ''}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feedback');
    }
  };

  // User actions
  const suspendUser = async (userId, suspend, reason = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/users/${userId}/suspension`,
        { status: suspend ? 'suspended' : 'active', suspension_reason: suspend ? reason : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(suspend ? 'User suspended' : 'User unsuspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const unlockUserAccount = async (email) => {
    if (!window.confirm(`Unlock account for ${email}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/unlock-account`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Account unlocked for ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock account');
    }
  };

  const resetUserPassword = async (email, newPassword) => {
    if (!newPassword || newPassword.length < 10) {
      toast.error('New password must be at least 10 characters');
      return false;
    }
    if (!window.confirm(`Reset password for ${email}?`)) return false;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/reset-user-password`,
        { email, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${email}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      return false;
    }
  };

  // Listing actions - Toggle admin visibility (admin_visible field)
  const toggleListingVisibility = async (vendorId, currentAdminVisible) => {
    try {
      const token = localStorage.getItem('token');
      const newAdminVisible = !currentAdminVisible;
      await axios.patch(
        `${API}/admin/listings/${vendorId}/visibility`,
        { admin_visible: newAdminVisible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newAdminVisible ? 'Vendor can now be visible in Browse' : 'Vendor hidden from Browse (Admin Override)');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update listing visibility');
    }
  };

  // Dispute actions
  const updateDisputeStatus = async (disputeId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/disputes/${disputeId}`,
        { dispute_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dispute marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update dispute');
    }
  };

  const resolveDisputeWithStatus = async (disputeId, inquiryId, newInquiryStatus) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.patch(`${API}/inquiries/${inquiryId}/status`, { status: newInquiryStatus }, { headers });
      await axios.patch(`${API}/admin/disputes/${disputeId}`, { dispute_status: 'resolved' }, { headers });
      
      toast.success(`Dispute resolved! Inquiry status changed to ${newInquiryStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve dispute');
    }
  };

  // Inquiry actions
  const overrideInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/inquiries/${inquiryId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Inquiry status changed to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inquiry status');
    }
  };

  const value = {
    // Data
    reviews,
    feedback,
    users,
    listings,
    disputes,
    activeInquiries,
    bookings,
    transactions,
    loading,
    
    // Filters
    selectedReviewFilter,
    setSelectedReviewFilter,
    selectedFeedbackFilter,
    setSelectedFeedbackFilter,
    includeCompletedInquiries,
    setIncludeCompletedInquiries,
    
    // Actions
    fetchData,
    approveReview,
    editReview,
    hideReview,
    updateFeedbackStatus,
    suspendUser,
    unlockUserAccount,
    resetUserPassword,
    toggleListingVisibility,
    updateDisputeStatus,
    resolveDisputeWithStatus,
    overrideInquiryStatus
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
